import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { UpgradePromptModal, type UpgradeReason } from "@/components/UpgradePromptModal";
import { useAuth } from "@/hooks/useAuth";
import { track, trackGatingDecision } from "@/lib/analytics";

interface PremiumGateApi {
  /** Opens the upgrade prompt for a given reason. */
  promptUpgrade: (reason: UpgradeReason, feature?: string) => void;
  /**
   * Returns true when the user may proceed. When they may not, the upgrade
   * prompt opens automatically — so call sites read as a plain guard.
   */
  requirePremium: (feature: string, reason?: UpgradeReason) => boolean;
  isPremium: boolean;
}

const PremiumGateContext = createContext<PremiumGateApi | null>(null);

export function PremiumGateProvider({ children }: { children: React.ReactNode }) {
  const { isPaid } = useAuth();
  const [prompt, setPrompt] = useState<{ reason: UpgradeReason; feature?: string } | null>(null);

  const promptUpgrade = useCallback((reason: UpgradeReason, feature?: string) => {
    setPrompt({ reason, feature });
    void track("upgrade_prompt_shown", { reason, feature });
  }, []);

  const requirePremium = useCallback(
    (feature: string, reason: UpgradeReason = "locked_feature") => {
      trackGatingDecision(feature, isPaid, { premium_access: isPaid, reason });
      if (isPaid) return true;
      promptUpgrade(reason, feature);
      return false;
    },
    [isPaid, promptUpgrade],
  );

  const value = useMemo<PremiumGateApi>(
    () => ({ promptUpgrade, requirePremium, isPremium: isPaid }),
    [promptUpgrade, requirePremium, isPaid],
  );

  return (
    <PremiumGateContext.Provider value={value}>
      {children}
      {prompt && (
        <UpgradePromptModal reason={prompt.reason} feature={prompt.feature} onClose={() => setPrompt(null)} />
      )}
    </PremiumGateContext.Provider>
  );
}

/**
 * Development-only recovery from a hot-reload context identity mismatch.
 *
 * When Vite replaces this module but not the tree that mounted the provider,
 * consumers read a *different* context object and see `null` even though the
 * provider is mounted correctly. Rather than crashing the preview, invalidate
 * the module so the whole provider tree remounts once.
 */
function recoverFromHmrMismatch(): void {
  const hot = (import.meta as unknown as { hot?: { invalidate?: (m?: string) => void } }).hot;
  if (!hot?.invalidate) return;
  console.warn("[premium-gate] context identity mismatch after HMR — remounting provider tree");
  hot.invalidate("premium gate context identity changed");
}

export function usePremiumGate(): PremiumGateApi {
  const ctx = useContext(PremiumGateContext);
  if (!ctx) {
    if (import.meta.env.DEV) {
      recoverFromHmrMismatch();
      // Safe, non-granting fallback so the dev preview keeps rendering while the
      // remount happens. Production still throws so real mistakes stay loud.
      return {
        promptUpgrade: () => {},
        requirePremium: () => false,
        isPremium: false,
      };
    }
    throw new Error("usePremiumGate must be used within PremiumGateProvider");
  }
  return ctx;
}